import log from 'loglevel';
import type { Logger } from 'loglevel';
import { normalizeCityName } from "../utils/cityName";
import { once } from "../utils/promise";

/**
 * Localidad tal y como se guarda en la base de datos.
 *
 * Es el esquema que produce CityService.normalizeCity a partir de la respuesta
 * REST, no el objeto crudo que devuelve el backend.
 */
export interface CityRecord {
    /** Clave primaria del almacen (keyPath: "id"). */
    id: number;
    name: string;
    /** Coordenadas [lon, lat]. */
    geometry: number[];
    population_size: number;
    priority: number;
}

/**
 * Error del objeto que ha disparado un evento de IndexedDB.
 *
 * Segun el evento, el target puede ser la peticion o la transaccion, y los dos
 * exponen `error`, pero TypeScript solo ve un EventTarget. La asercion se hace
 * una vez aqui en lugar de repetirla en cada callback. Donde el target es la
 * propia peticion se usa `request.error` directamente y no hace falta.
 */
function eventError(event: Event): DOMException | null {
    return (event.target as IDBRequest | IDBTransaction | null)?.error ?? null;
}

/**
 * Storage service for city data.
 *
 * Use indexedDB.
 *
 * Implements most Storage methods plus some utility functions.
 *
 * OJO: al contrario que el resto de storages, esta clase no hereda de
 * BaseStorage. De la clase base solo usaba logger(): no implementa storage(),
 * no usa keyPrefix, y setItem()/clear() tienen aqui otra firma y otra semantica
 * (son asincronas, y setItem recibe la localidad entera en vez de clave y
 * valor). Heredarla daba error TS2417 por incompatibilidad del lado estatico, y
 * ademas arrastraba unos getItem/getObject trampa, que habrian reventado contra
 * el throw del storage() abstracto.
 */
class CityStorage {
    /**
     * IndexedDB instance
     */
    static dbName = 'GronzeMapsCities';

    /**
     * IndexedDB instance
     */
    static db: IDBDatabase | undefined;

    /**
     * Pending indexedDB initialization.
     */
    static initPromise: Promise<void> | undefined;

    /**
     * Get logger.
     */
    static logger(): Logger {
        return log.getLogger('CityStorage');
    }

    /**
     * Init indexedDB.
     */
    static init(): Promise<void> {
        if (this.db) {
            return Promise.resolve();
        }

        return once(this, 'initPromise', () => this.openDatabase());
    }

    /**
     * Opens indexedDB and creates its schema when needed.
     */
    static openDatabase(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName);
            request.onerror = () => {
                this.logger().error('Creating indexedDB city database error', request.error);
                reject(request.error);
            };
            request.onsuccess = () => {
                const db = request.result;
                this.db = db;
                this.logger().debug('IndexedDB city database created');
                db.onerror = (event) => {
                    console.error(`IndexedDB error: ${eventError(event)?.message}`);
                };
                db.onversionchange = () => {
                    db.close();
                    if (this.db === db) {
                        this.db = undefined;
                    }
                };
                db.onclose = () => {
                    if (this.db === db) {
                        this.db = undefined;
                    }
                };
                resolve();
            };
            request.onupgradeneeded = () => {
                const db = request.result;
                const objectStore = db.createObjectStore(this.dbName, { keyPath: "id" });
                objectStore.createIndex("id", "id", { unique: true });
                objectStore.createIndex("name", "name", { unique: false });
                objectStore.createIndex("population_size", "population_size", { unique: false });
                objectStore.createIndex("priority", "priority", { unique: false });
                objectStore.createIndex("geometry", "geometry", { unique: false });

                objectStore.transaction.oncomplete = () => {
                    this.logger().debug('City database schema created');
                };
            }
        });
    }

    /**
     * Closes the current connection and opens a new one.
     */
    static async reconnect(): Promise<void> {
        const db = this.db;
        this.db = undefined;
        db?.close();
        await this.init();
    }

    /**
     * Implements Storage.setItem().
     *
     * OJO: hoy no lo llama nadie. Quien guarda localidades es CityService, y lo
     * hace por lotes con setItems().
     */
    static async setItem(cityData?: CityRecord): Promise<boolean> {
        if (!cityData) {
            console.warn('No city data provided: nothing stored', cityData);
            return false;
        }

        await this.setItems([cityData]);
        return true;
    }

    /**
     * Stores a city list in a single transaction.
     */
    static setItems(cityList: CityRecord[]): Promise<number> {
        return new Promise((resolve, reject) => {
            const db = this.db;
            if (!db) {
                reject("Database is not initialized. Call CityStorage.init() first.");
                return;
            }

            if (cityList.length === 0) {
                resolve(0);
                return;
            }

            const transaction = db.transaction([this.dbName], "readwrite");
            const objectStore = transaction.objectStore(this.dbName);

            cityList.forEach((cityData) => objectStore.put(cityData));

            transaction.oncomplete = () => {
                resolve(cityList.length);
            };

            transaction.onerror = (event) => {
                reject(eventError(event));
            };

            transaction.onabort = (event) => {
                reject(eventError(event));
            };
        });
    }

    /**
     * Finds all items that include the given string in their "name" property.
     */
    static find(searchString: string): Promise<CityRecord[]> {
        return new Promise((resolve, reject) => {
            const db = this.db;
            if (!db) {
                reject("Database is not initialized. Call CityStorage.init() first.");
                return;
            }

            const transaction = db.transaction([this.dbName], "readonly");
            const objectStore = transaction.objectStore(this.dbName);
            const nameIndex = objectStore.index("name");

            const results: CityRecord[] = [];

            const cursorRequest = nameIndex.openCursor();
            cursorRequest.onsuccess = () => {
                const cursor = cursorRequest.result;
                if (cursor) {
                    const city = cursor.value as CityRecord;
                    const name = normalizeCityName(city.name);
                    if (name.includes(searchString)) {
                        results.push(city);
                    }
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };

            transaction.onerror = (event) => {
                reject(`Transaction error: ${eventError(event)?.message}`);
            };
        });
    }

    /**
     * Implements Storage.count()
     * Returns the total number of cities in db.
     */
    static count(): Promise<number> {
        return new Promise((resolve, reject) => {
            const db = this.db;
            if (!db) {
                reject("Database is not initialized. Call CityStorage.init() first.");
                return;
            }

            const transaction = db.transaction([this.dbName], "readonly");
            const objectStore = transaction.objectStore(this.dbName);

            const countRequest = objectStore.count();

            countRequest.onsuccess = () => {
                resolve(countRequest.result);
            };

            countRequest.onerror = (event) => {
                reject(`Count error: ${eventError(event)?.message}`);
            };
        });
    }

    /**
     * Implements Storage.clear()
     * Removes all records from the cyties db.
     */
    static clear(): Promise<string> {
        this.logger().debug('Deleting all cities on DB');
        return new Promise((resolve, reject) => {
            const db = this.db;
            if (!db) {
                reject("Database is not initialized. Call CityStorage.init() first.");
                return;
            }

            const transaction = db.transaction([this.dbName], "readwrite");
            const objectStore = transaction.objectStore(this.dbName);

            const clearRequest = objectStore.clear();

            clearRequest.onsuccess = () => {
                resolve("All records have been cleared.");
            };

            clearRequest.onerror = (event) => {
                reject(`Clear error: ${eventError(event)?.message}`);
            };
        });
    }
}

export default CityStorage;
